import React, { useEffect, useState } from 'react';
import {
  Row,
  Col,
  Card,
  Tag,
  Tooltip,
  Popconfirm,
  Typography,
  Spin,
  message,
  Menu,
  Dropdown,
  Carousel,
} from "antd";
import PaginationTable from "../../../shared/PaginationTable";
import moment from "moment";
import {
  TableColumnTitle,
  PrimaryTableRowText,
} from "../../../shared/globalStyling/styledText";
import { hasIsAmplifyDefaultSelection } from "../../../../core/utils/orgSettings";
import {
  EditOutlined,
  NotificationOutlined,
  PlusOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { EllipsisOutlined } from "@ant-design/icons";
import { CreativeAdGroupForm } from "./CreativeAdGroupForm";
import { CreativeAdGroupAddCreativesModal } from "./CreativeAdGroupAddCreativesModal";
import CreativePreviewModal from "./CreativePreviewModal";
import { multiImageZipKey } from "../../../../core/utils/dataloading/multiImageZipKey";
import "./../../Creatives/library/creativestyle.css";

import Button from "../../../shared/globalStyling/Button";
import { useHistory } from "react-router-dom";
import SearchBar from "../../Creatives/library/SearchBar";
import MediaFetcher from "../../../ux/Creatives/library/MediaFetcher";
import { performCreativeSearch } from "../../Creatives/utils/mediaUtils";
const { Title } = Typography;

const CreativeAdGroupTable = ({
  adGroupCreatives,
  onDeleteAdgroup,
  setDefaultAmplifyCreatives,
  amplify,
  selectedOrg,
  updateAdGroup,
  orgMedias,
  setCreativeKeys,
  currentPage: propCurrentPage,
  onPageChange,
}) => {
  const history = useHistory();
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(propCurrentPage || 1);
  const [ids, setIds] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [filteredAdGroups, setFilteredAdGroups] = useState(adGroupCreatives || []);
  const [visibleEditAdGroupForm, setVisibleEditAdGroupForm] = useState(false);
  const [visibleAddAdGroupForm, setVisibleAddAdGroupForm] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [previewImages, setPreviewImages] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewStartIndex, setPreviewStartIndex] = useState(0);
  const [modalKey, setModalKey] = useState(0);

  // Sync currentPage with prop
  useEffect(() => {
    if (propCurrentPage && propCurrentPage !== currentPage) {
      setCurrentPage(propCurrentPage);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propCurrentPage]);

  useEffect(() => {
    // Guard against undefined adGroupCreatives
    if (!adGroupCreatives) {
      setFilteredAdGroups([]);
      return;
    }

    // Apply base filters first
    const baseFiltered = adGroupCreatives
      .filter(
        adGroup =>
          adGroup.org.length === 1 ||
          (selectedOrg &&
            selectedOrg.parentOrg &&
            selectedOrg.parentOrg.length > 0)
      )
      // Remove amplify filter - show all ad groups regardless of amplify permissions
      // The "Set as Amplify Default" action will be disabled for non-amplify users in the menu
      .filter(
        adGroup => !adGroup.medias.some(media => media.fileType === "MP4")
      )
      // Filter out empty ad groups (groups with no medias)
      .filter(adGroup => Array.isArray(adGroup.medias) && adGroup.medias.length > 0);

    // Apply search filter if search text exists
    const searchFiltered = performCreativeSearch(baseFiltered, searchText, 'adGroup');

    setFilteredAdGroups(searchFiltered);
  }, [searchText, adGroupCreatives, amplify, selectedOrg]);

  const handleSearch = value => {
    setSearchText(value);
  };

  const handleImageClick = (clickedMedia, allImages) => {
    const clickedIndex = allImages.findIndex(img => img.key === clickedMedia.key);
    setPreviewImages(allImages);
    setPreviewStartIndex(clickedIndex);
    setPreviewVisible(true);
    setModalKey(prev => prev + 1); // Force modal recreation
  };
  const downloadCreatives = async record => {
    const fileKeys = record.medias.map(media => media.key);
    if (!fileKeys.length) {
      return message.warning('No creatives to download');
    }

    const rawFilename = record && record.name ? record.name : 'creatives';

    // Sanitize filename
    const filename = rawFilename.replace(/[^a-z0-9]/gi, '_').toLowerCase();

    // Download files and generate zipped file
    const results = await multiImageZipKey(filename, fileKeys);

    // Check for complete failure
    if (results.success === false) {
      const failedCount = Array.isArray(results.failedDownloads)
        ? results.failedDownloads.length
        : results.failedDownloads;
      message.error(
        `Failed to download ${failedCount} file(s). Files may be deleted or inaccessible.`
      );
    }
    // Check for partial failure (some succeeded, some failed)
    else if (
      (Array.isArray(results.failedDownloads) && results.failedDownloads.length > 0) ||
      (typeof results.failedDownloads === 'number' && results.failedDownloads > 0) ||
      results.failedKeys?.length > 0
    ) {
      const successCount = results.successfulDownloads || 0;
      const failedCount = Array.isArray(results.failedDownloads)
        ? results.failedDownloads.length
        : results.failedDownloads || results.failedKeys?.length || 0;
      message.warning(
        `Partial download: ${successCount} creative(s) downloaded successfully, ${failedCount} failed. Failed files may be deleted or inaccessible.`
      );
    }
    // Complete success
    else {
      const successCount = results.successfulDownloads || fileKeys.length;
      message.success(`Successfully downloaded ${successCount} creative(s)`);
    }
  };

  const handleDownloadAdUnits = async () => {
    if (!ids || ids.length === 0) {
      return message.warning("Please select ad groups to download");
    }

    if (!selectedRows || selectedRows.length === 0) {
      return message.warning("Please select ad groups to download");
    }

    const allMediaKeys = selectedRows
      .flatMap(adGroup => adGroup.medias || [])
      .map(media => media.key);

    if (allMediaKeys.length === 0) {
      return message.warning("No creatives found to download");
    }

    const adGroupNames = selectedRows.map(adGroup => adGroup.name).join(", ");
    const fileName = `Display Ad Groups - ${adGroupNames} (${allMediaKeys.length} creatives)`;

    setDownloadLoading(true);
    try {
      const results = await multiImageZipKey(fileName, allMediaKeys);

      // Check for complete failure
      if (results.success === false) {
        const failedCount = Array.isArray(results.failedDownloads)
          ? results.failedDownloads.length
          : results.failedDownloads;
        message.error(
          `Failed to download ${failedCount} file(s). Files may be deleted or inaccessible.`
        );
      }
      // Check for partial failure (some succeeded, some failed)
      else if (
        (Array.isArray(results.failedDownloads) && results.failedDownloads.length > 0) ||
        (typeof results.failedDownloads === 'number' && results.failedDownloads > 0) ||
        results.failedKeys?.length > 0
      ) {
        const successCount = results.successfulDownloads || 0;
        const failedCount = Array.isArray(results.failedDownloads)
          ? results.failedDownloads.length
          : results.failedDownloads || results.failedKeys?.length || 0;
        message.warning(
          `Partial download: ${successCount} creative(s) downloaded successfully, ${failedCount} failed. Failed files may be deleted or inaccessible.`
        );
      }
      // Complete success
      else {
        const successCount = results.successfulDownloads || allMediaKeys.length;
        message.success(
          `Successfully downloaded ${successCount} creatives`
        );
      }
    } catch (error) {
      message.error("Failed to download creatives");
    } finally {
      setDownloadLoading(false);
    }
  };

  let columns = [
    {
      title: <TableColumnTitle text={"Display Ad Group Name"} />,
      dataIndex: "name",
      key: "id",
      width: 200,
      align: "left",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name, record) => {
        return (
          <>
            <div
              onClick={() => {
                // Use RESTful URL pattern - this table shows display ad groups
                history.push(`/creatives/display/groups/${record.id}`);
              }}
              style={{ cursor: 'pointer' }}
            >
              <PrimaryTableRowText text={name} />
            </div>
            {record.isAmplifyDefault && amplify ? (
              <Tag color="green">Amplify Default</Tag>
            ) : null}
          </>
        );
      },
    },
    {
      title: <TableColumnTitle text={"Ad Units"} />,
      dataIndex: "media",
      key: "adUnits",
      width: 100,
      align: "left",
      render: (_, record) => {
        return record.medias.length;
      },
    },
    {
      title: <TableColumnTitle text={"Gallery"} />,
      dataIndex: "medias",
      key: "medias",
      width: 300, // Much wider for gallery carousel
      align: "left",
      render: (medias, record) => {
        const uniqueMedias = Array.isArray(medias)
          ? Array.from(
              new Map(
                medias.map(media => [media.id || media.key, media])
              ).values()
            )
          : [];

        // Return early if no medias to avoid carousel errors
        if (!uniqueMedias || uniqueMedias.length === 0) {
          return <div>No images</div>;
        }

        // Responsive image display based on screen size
        const itemCount = uniqueMedias.length;

        // Use CSS media queries concept but with container width (SSR-safe)
        const getMaxVisible = () => {
          const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
          if (screenWidth < 480) return 2; // Mobile: 2 images + count
          if (screenWidth < 768) return 3; // Small tablet: 3 images + count
          return 5; // Desktop: 5 images + count
        };

        const maxVisible = getMaxVisible();
        const visibleItems = uniqueMedias.slice(0, maxVisible);
        const remainingCount = itemCount - maxVisible;

        // Compact image sizes (SSR-safe)
        const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
        const imageSize = screenWidth < 480 ? "45px" : "60px";
        const gap = screenWidth < 480 ? "5px" : "7px";

        return (
          <div style={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: gap,
          }}>
            {visibleItems.map(media => (
              <div
                key={media.id || media.key}
                onClick={() => handleImageClick(media, uniqueMedias)}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: imageSize,
                  width: imageSize,
                  minWidth: imageSize, // Prevent shrinking
                  background: "#f5f5f5",
                  border: "1px solid #ddd",
                  overflow: "hidden",
                  flexShrink: 0,
                  cursor: "pointer",
                  transition: "transform 0.2s ease",
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.05)"}
                onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
              >
                <MediaFetcher
                  mediaKey={media.key}
                  width="100%"
                  height={imageSize}
                  objectFit="contain"
                />
              </div>
            ))}
            {remainingCount > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: imageSize,
                  width: imageSize,
                  minWidth: imageSize, // Prevent shrinking
                  background: "#f0f0f0",
                  border: "1px solid #ddd",
                  flexShrink: 0,
                  fontSize: screenWidth < 480 ? "9px" : "11px",
                  fontWeight: "bold",
                  color: "#666"
                }}
              >
                +{remainingCount}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: <TableColumnTitle text={"Last Updated"} />,
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 180, // Extra wide for full date display
      align: "left",
      sorter: (a, b) => {
        const dateA = moment(a.updatedAt);
        const dateB = moment(b.updatedAt);
        return dateB.isBefore(dateA) ? -1 : dateB.isAfter(dateA) ? 1 : 0; // Most recent first
      },
      defaultSortOrder: 'ascend', // This will show most recent first due to our sorter logic
      render: updatedAt => {
        return (
          <PrimaryTableRowText
            text={moment(updatedAt).format("MM/DD/YYYY hh:mm A")}
          />
        );
      },
    },
    {
      title: <TableColumnTitle text={"Actions"} />,
      dataIndex: "actions",
      key: "actions",
      width: 80, // Even wider for actions menu
      align: "center",
      render: (_, record) => {
        const handleMenuClick = event => {
          const { key } = event;

          switch (key) {
            // AMPLIFY DEFAULT
            case '1':
              selectDefaultCreative(record);
              break;

            case '2':
              setVisibleEditAdGroupForm(true);
              setSelectedRows([record]);
              break;

            case '3':
              setVisibleAddAdGroupForm(true);
              setSelectedRows([record]);
              break;
            case '4':
              downloadCreatives(record);
              break;

            default:
              break;
          }
        };

        const menu = (
          <Menu onClick={handleMenuClick}>
            {amplify && (
              <Menu.Item key="1" icon={<NotificationOutlined />} disabled={record.isAmplifyDefault}>
                Set as Amplify Default
              </Menu.Item>
            )}
            <Menu.Item key="2" icon={<EditOutlined />}>
              Edit
            </Menu.Item>
            <Menu.Item key="3" icon={<PlusOutlined />}>
              Add Creatives
            </Menu.Item>
            <Menu.Item key="4" icon={<DownloadOutlined />}>
              Download Creatives
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button style={{ minWidth: 0 }}>
              <EllipsisOutlined style={{ transform: 'rotate(90deg)' }} />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  const rowSelection = {
    onChange: (_, _selectedRows) => {
      setSelectedRows(_selectedRows);
      const ids = _selectedRows.map((row) => {
        return row.id;
      });
      setIds(ids);
    },
  };

  const selectDefaultCreative = async (record) => {
    setLoading(true);
    await setDefaultAmplifyCreatives(record);
    setLoading(false);
  };

  return (
    <>
      <Row>
        <Col xs={24} xl={24}>
          <Card bodyStyle={{ padding: 10 }}>
            <Row align="middle" className="ant-row-justify-space-between">
              <Col>
                <Title level={4}>Display Ad Groups</Title>
              </Col>
              <Col>
                <SearchBar searchText={searchText} onSearch={handleSearch} />
              </Col>
            </Row>
            <Row align="middle" className="ant-row-justify-space-between">
              <Col xs={24} sm={16} md={16} lg={14}>
                <Row>
                  <Popconfirm
                    placement="topLeft"
                    title={`Are you sure you want to delete the selected Ad Group?`}
                    onConfirm={() => {
                      if (amplify && hasIsAmplifyDefaultSelection(selectedRows, true)) {
                        return message.error(
                          "One of the selection is an Amplify Default and can't be deleted. Please update the selection and try again."
                        );
                      }
                      setIds(onDeleteAdgroup(ids));
                    }}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Tooltip placement="bottom" title={`Delete selected creatives`}>
                      <Button
                        type="default"
                        disabled={ids && ids.length > 0 ? false : true}
                      >
                        Delete
                      </Button>
                    </Tooltip>
                  </Popconfirm>
                  <Button
                    type="default"
                    disabled={ids && ids.length > 0 && !downloadLoading ? false : true}
                    loading={downloadLoading}
                    onClick={handleDownloadAdUnits}
                  >
                    Download AD Units
                  </Button>
                </Row>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
      <Spin tip="Loading..." spinning={isLoading}>
        <PaginationTable
          bordered={false}
          footer={
            filteredAdGroups && filteredAdGroups.slice(currentPage * 10 - 10, currentPage * 10)
          }
          columns={columns}
          data={filteredAdGroups}
          scroll={{ x: true, y: false }}
          tableLayout="auto"
          paginationSize={'default'}
          pageSize={pageSize}
          size={'default'}
          total={filteredAdGroups && filteredAdGroups.length}
          current={currentPage}
          rowSelection={rowSelection}
          onChange={(paginationObject) => {
            setPageSize(paginationObject.pageSize);
            setCurrentPage(paginationObject.current);
            if (onPageChange) {
              onPageChange(paginationObject.current);
            }
          }}
        />
      </Spin>

      {visibleEditAdGroupForm && (
        <CreativeAdGroupForm
          visible={visibleEditAdGroupForm}
          setVisible={setVisibleEditAdGroupForm}
          selectedAdGroup={selectedRows[0]}
          updateAdGroup={updateAdGroup}
          allAdGroups={filteredAdGroups}
        />
      )}
      {visibleAddAdGroupForm && (
        <CreativeAdGroupAddCreativesModal
          visible={visibleAddAdGroupForm}
          setVisible={setVisibleAddAdGroupForm}
          selectedAdGroup={selectedRows[0]}
          orgMedias={orgMedias}
          setCreativeKeys={setCreativeKeys}
          updateAdGroup={updateAdGroup}
        />
      )}

      <CreativePreviewModal
        key={`display-modal-${modalKey}`}
        visible={previewVisible}
        onClose={() => setPreviewVisible(false)}
        images={previewImages}
        startIndex={previewStartIndex}
      />
    </>
  );
};

export default CreativeAdGroupTable;
